import React from 'react';
import { injectIntl } from 'react-intl';
import Select from '../../../components/Select';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { testScoresByOption, optionsByName, scaleByType } from './constants';
import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
} from '../../../context/host/consumer';

import './academicMatchGraph.css';
import PropTypes from 'prop-types';
import BarGraph from './bargraph';

class AcademicMatchGraph extends React.PureComponent {
  static propTypes = {
    name: PropTypes.string,
    graphData: PropTypes.object,
    intl: PropTypes.object,
  };

  constructor(props) {
    super(props);

    const defaultGPA = get(props, 'graphData.highSchool.hsoptions.defaultGPA', 0);
    this.state = {
      gpaOption: defaultGPA === 0 ? 'cumulativeGpa' : 'weightedGpa',
      satOption: 'satTotal',
      actOption: 'actComposite',
    };
  }

  onOptionChange = (event, { value }) => {
    const { name } = this.props;
    const newState = {};
    newState[`${name.toLowerCase()}Option`] = value;
    this.setState(newState);
    event.preventDefault();
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location: `${trackingLocations.COMPARE_ME}_${value}`,
    });
  };

  getScaleByNameAndOption = (name, option) => {
    const gpaMaps = get(this.props.graphData, 'gpaConcordance.concordanceMaps', null);
    const fixGpaBarGraphWithoutPeerMap = get(
      this.props.graphData,
      'fixGpaBarGraphWithoutPeerMap',
      false,
    );
    const weighted = option.startsWith('weightedGpa');
    if (name === 'GPA' && gpaMaps) {
      const peerGpaMaps = get(gpaMaps, 'peerGpaMaps', null);
      const highSchoolId = get(
        this.props.graphData,
        'highSchool.hsoptions.highSchoolId',
        null,
      );
      const peerGpaMap =
        peerGpaMaps && highSchoolId
          ? Object.values(peerGpaMaps || {}).find(
              (peerMap) => peerMap.highschoolId === highSchoolId,
            )
          : null;
      const gpaMap = get(gpaMaps, 'gpaMap', null);
      const wgpaMap = get(gpaMaps, 'wgpaMap', null);
      let peerMap = peerGpaMap || gpaMap || wgpaMap;
      let nationalMap = gpaMaps.standardGpaMap;
      if (weighted) {
        peerMap = peerGpaMap || wgpaMap || gpaMap;
      }
      if (!peerGpaMap && peerMap && peerMap.upper13 < 6 && peerMap.upper13 > 3.9) {
        if (peerMap.upper13 > 4.5) {
          peerMap = gpaMaps.standardWgpaMap;
        } else {
          peerMap = gpaMaps.standardGpaMap;
        }
      }
      let peerHigh = peerMap ? peerMap.upper12 : null;
      let peerLow = peerMap ? peerMap.upper2 : null;
      const isPeerLowEmpty = fixGpaBarGraphWithoutPeerMap
        ? peerLow == null
        : peerLow === undefined;
      const isPeerHighEmpty = fixGpaBarGraphWithoutPeerMap
        ? peerHigh == null
        : peerHigh === undefined;
      if (
        isPeerLowEmpty ||
        Number.isNaN(peerHigh) ||
        isPeerHighEmpty ||
        Number.isNaN(peerLow)
      ) {
        peerLow = NaN;
        peerHigh = NaN;
      }
      return {
        low: nationalMap.upper2,
        high: nationalMap.upper12,
        peerLow: peerLow,
        peerHigh: peerHigh,
      };
    } else if (name === 'GPA' && weighted) {
      const graphGpaMax = get(
        this.props,
        'graphData.highSchool.hsoptions.graphGpaMax',
        4,
      );
      return { low: 0, high: graphGpaMax };
    }
    const name2use = name === 'SAT' && option !== 'satTotal' ? 'SATOther' : name;
    return scaleByType[name2use];
  };

  getAllOptionsGraphData = () => {
    const { name, graphData } = this.props;
    const suffix = name === 'GPA' && graphData.useGpaConcordance ? '_NEW' : '';
    let options = optionsByName[`${name}${suffix}`];

    if (name === 'GPA') {
      // Use config properties from Succeed to determine which options are allowed
      // If allowGpaToggle === false, the only option is the defaultGPA option
      const allowGpaToggle = get(
        graphData.highSchool,
        'options.graphAllowGPAToggle',
        true,
      );

      if (!allowGpaToggle) {
        const option = this.state[`${name.toLowerCase()}Option`];
        options = [options.find((o) => o.value === option)];
      }
    }

    const barGraphData = {};
    /* istanbul ignore else */
    if (options && options.length > 0) {
      options.forEach((opt) => {
        let yourScore, national25, national75;
        const scale = this.getScaleByNameAndOption(name, opt.value);

        yourScore = graphData.you[opt.value] || 0;
        if (yourScore < scale.low) {
          yourScore = 0;
        }

        if (name === 'GPA') {
          national25 = Number(get(graphData, 'national.gpa[25].value', 0));
          national75 = Number(get(graphData, 'national.gpa[75].value', 0));
        } else {
          const testScores =
            graphData.national && graphData.national.testScores
              ? graphData.national.testScores.find(
                  (s) => s.type === testScoresByOption[opt.value],
                )
              : null;
          national25 = testScores && testScores[25] ? Number(testScores[25]) : 0;
          national75 = testScores && testScores[75] ? Number(testScores[75]) : 0;
        }
        const accepted = get(graphData, `applicationStatistics.accepted`, {});
        const hsTestScores =
          name === 'GPA'
            ? accepted[opt.value]
            : get(accepted, name.toLowerCase(), {})[opt.value];
        const highschool25 =
          hsTestScores && hsTestScores[25] ? Number(hsTestScores[25]) : 0;
        const highschool75 =
          hsTestScores && hsTestScores[75] ? Number(hsTestScores[75]) : 0;

        if (
          yourScore > 0 ||
          (highschool25 >= 0 && highschool75 > 0) ||
          (national25 >= 0 && national75 > 0)
        ) {
          barGraphData[opt.value] = {
            option: opt.value,
            usePsat: name === 'SAT' && graphData.usePsat,
            useGpaConcordance: graphData.useGpaConcordance,
            plotData: {
              low: scale.low,
              high: scale.high,
              peerLow: scale.peerLow,
              peerHigh: scale.peerHigh,
            },
            nationalData: {
              low: Math.round(national25 * 100) / 100,
              high: Math.round(national75 * 100) / 100,
            },
            schoolData: {
              low: Math.round(highschool25 * 100) / 100,
              high: Math.round(highschool75 * 100) / 100,
            },
            userData: Math.round(yourScore * 100) / 100,
          };
        }
      });
    }

    return barGraphData;
  };

  render() {
    const { name } = this.props;
    const barGraphData = this.getAllOptionsGraphData();
    if (isEmpty(barGraphData)) {
      return null;
    }

    const optionsWithValues = Object.keys(barGraphData);
    const options = optionsByName[name].filter(
      (opt) => opt && optionsWithValues.includes(opt.value),
    );
    let option = this.state[`${name.toLowerCase()}Option`];
    /* istanbul ignore if */
    if (!options.some((o) => o.value === option)) {
      option = options[0].value;
    }

    return (
      <div className="academic-match-graph-container">
        <div
          className="academic-match-dropdown-container"
          data-cy="academic-match-graphs-cell-right"
        >
          <div className="academic-match-graphs-name">{name}</div>
          <Select
            data-cy="academic-match-graphs-dropdown"
            className="academic-match-graphs-dropdown"
            icon="chevron down"
            options={options}
            value={option}
            onChange={this.onOptionChange}
            aria-label={`${name} chart`}
            upward={false}
          />
        </div>
        <div
          className="academic-match-bargraph-container"
          data-cy="academic-match-graphs-cell-right"
        >
          <BarGraph graphData={barGraphData[option]} />
        </div>
      </div>
    );
  }
}

export default injectIntl(withHostContext(AcademicMatchGraph));
